package org.matthewo.pages.event.tabs

import emotion.react.css
import js.objects.buildReadonlyRecord
import js.objects.unsafeJso
import org.matthewo.MATCH_SEARCH
import org.matthewo.api.EventTeamHistogram
import org.matthewo.components.Slider
import org.matthewo.js.toFixed
import org.matthewo.pages.event.EVENT_PATH
import org.matthewo.pages.event.EventTabFC
import org.matthewo.pages.event.useEventHeader
import org.matthewo.pages.team.TEAM_PARAM
import org.matthewo.recharts.cartesian.CartesianGrid
import org.matthewo.recharts.cartesian.Line
import org.matthewo.recharts.cartesian.XAxis
import org.matthewo.recharts.cartesian.YAxis
import org.matthewo.recharts.chart.LineChart
import org.matthewo.recharts.component.Label
import org.matthewo.recharts.component.Tooltip
import org.matthewo.recharts.component.TooltipProps
import org.matthewo.recharts.container.Legend
import org.matthewo.recharts.container.ResponsiveContainer
import org.matthewo.recharts.type.*
import org.matthewo.router.div
import org.matthewo.routes.API
import org.matthewo.styles.BACKGROUND_COLOR
import org.matthewo.styles.TITLE_FONT_COLOR
import react.FC
import react.dom.html.ReactHTML.b
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.p
import react.useMemo
import tanstack.react.router.useNavigate
import tanstack.react.router.useParams
import tanstack.react.router.useSearch
import tanstack.router.core.RoutePath
import web.cssom.Border
import web.cssom.ClassName
import web.cssom.Display
import web.cssom.FlexDirection
import web.cssom.FontWeight
import web.cssom.LineStyle
import web.cssom.NamedColor
import web.cssom.Overflow
import web.cssom.array
import web.cssom.em
import web.cssom.number
import web.cssom.pct
import web.cssom.px
import kotlin.Boolean
import kotlin.Float
import kotlin.Int
import kotlin.Pair
import kotlin.String
import kotlin.arrayOf
import kotlin.collections.List
import kotlin.collections.listOf
import kotlin.collections.map
import kotlin.collections.mapIndexed
import kotlin.collections.runningReduce
import kotlin.collections.toList
import kotlin.collections.toTypedArray
import kotlin.let
import kotlin.math.roundToInt
import kotlin.text.toIntOrNull
import kotlin.time.Duration.Companion.milliseconds
import kotlin.to

val EVENT_TEAM_RANKING_PLOT_PATH = RoutePath(EVENT_PATH / "rankings" / TEAM_PARAM)

val EventTeamRankingPlot = EventTabFC<API.Event.Rankings.Team, EventTeamHistogram>({ event, params, searchParams ->
    val team = params[TEAM_PARAM]?.toIntOrNull() ?: return@EventTabFC null
    API.Event.Rankings.Team(event, team, searchParams[MATCH_SEARCH]?.toString()?.toIntOrNull())
}) { (coroutineScope, year, eventCode, data) ->

    val navigate = useNavigate()
    val pathParams = useParams()
    val searchParams = useSearch()
    val title = data?.event?.let { event -> event + (searchParams[MATCH_SEARCH]?.let { match -> ": After Match $match" } ?: "") }
    useEventHeader(data?.years, year, eventCode, title, data)

    val number = pathParams[TEAM_PARAM]?.toIntOrNull()
    val name = data?.name
    val histogram = data?.histogram

    div {
        css {
            display = Display.flex
            flexDirection = FlexDirection.column
            gap = 1.em
            width = 100.pct
            height = 100.pct
        }
        if (data?.numMatches?.let { it >= 1 } == true) {
            Slider {
                initial = searchParams["match"]?.toString()?.toIntOrNull() ?: data.numMatches
                min = 0
                max = data.numMatches
                steps = data.numMatches
                dragTimeout = 500.milliseconds
                onChange = { value ->
                    navigate {
                        search = buildReadonlyRecord {
                            this["match"] = value.roundToInt().toString()
                        }
                        replace = true
                    }
                }
            }
        }
        RankingPlot {
            team = "${number ?: ""} - ${name ?: ""}"
            this.histogram = histogram
        }
    }
}

external interface RankingTooltipProps : TooltipProps {
    var team: String
    var histogram: List<Float>?
}

val RankingPlot = FC<RankingTooltipProps>("RankingPlot") { props ->
    val histogramData = props.histogram?.mapIndexed { i, p -> i + 1 to p * 100 }?.toList()?.toTypedArray() ?: []
    val sums = useMemo(props.histogram) {
        props.histogram?.runningReduce(Float::plus)?.map { it * 100 } ?: []
    }

    val EventTeamRankingTooltip = FC("EventTeamRankingTooltip") { propsTooltip: dynamic ->
        if (!propsTooltip["active"].unsafeCast<Boolean>()) return@FC
        val payload = propsTooltip["payload"][0]
        if (undefined == payload) return@FC
        val instance = payload["payload"].unsafeCast<Pair<Int, Float>>()
        div {
            css {
                opacity = number(0.9)
                background = BACKGROUND_COLOR
                border = Border(2.px, LineStyle.solid, TITLE_FONT_COLOR)
                borderRadius = 4.px
                color = NamedColor.white
                padding = array(0.0.em, 0.5.em)

                ".label" {
                    fontWeight = FontWeight.bold
                }
            }
            p {
                className = ClassName("label")
                b {
                    +props.team
                }
            }
            p {
                +"${instance.second.toFixed(1)}% chance of Rank ${instance.first}"
            }
            p {
                +"${sums[instance.first - 1].toFixed(1)}% chance of Rank <= ${instance.first}"
            }
        }
    }

    div {
        css {
            width = 100.pct
            height = 100.pct
            fontSize = 1.em
            overflow = Overflow.hidden
        }
        ResponsiveContainer {
            width = 100.pct
            height = 100.pct
            LineChart {
                margin = ChartMargin(10, 20, 0, 15)
                this.data = histogramData
                CartesianGrid {
                    strokeDasharray = "3 3"
                    vertical = false
                }
                XAxis {
                    Label {
                        value = "Rank"
                        position = ComponentPosition.bottom
                        offset = -20
                    }
                    type = AxisType.category
                    height = 50
                    dataKey = { instance: Pair<Int, Float> ->
                        instance.first
                    }
                }
                YAxis {
                    Label {
                        value = "Percentage"
                        position = ComponentPosition.insideLeft
                        angle = -90
                        style = unsafeJso {
                            textAnchor = "middle"
                        }
                    }
                    width = 60
                }
                Legend {
                    layout = LayoutDirection.horizontal
                    verticalAlign = VerticalAlign.bottom
                    height = 30
                }
                Line {
                    type = LineType.monotone
                    dataKey = { instance: Pair<Int, Float> ->
                        instance.second
                    }
                    stroke = TITLE_FONT_COLOR.toString()
                    strokeWidth = 2
                    name = props.team
                }
                Tooltip {
                    content = EventTeamRankingTooltip
                }
            }
        }
    }
}
